/**
 * Domain Layer - CsvData Entity
 * CSVデータエンティティ定義
 */

/**
 * CSVデータを表すエンティティ
 */
export interface CsvData {
  /** ヘッダー行（列名の配列） */
  readonly headers: readonly string[];
  /** データ行（2次元配列） */
  readonly rows: readonly (readonly string[])[];
  /** 総行数（ヘッダーを除く） */
  readonly totalRows: number;
  /** 元のファイル名 */
  readonly fileName: string;
}

/**
 * CSVパース時のエラー情報
 */
export interface CsvParseInfo {
  /** 警告メッセージ */
  readonly warnings: readonly string[];
  /** エラーメッセージ */
  readonly errors: readonly string[];
}

/**
 * CsvDataエンティティのファクトリ関数
 * @param headers ヘッダー行
 * @param rows データ行
 * @param fileName ファイル名
 * @returns CsvDataエンティティ
 */
export const createCsvData = (
  headers: string[],
  rows: string[][],
  fileName: string
): CsvData => ({
  headers: Object.freeze([...headers]),
  rows: Object.freeze(rows.map((row) => Object.freeze([...row]))),
  totalRows: rows.length,
  fileName,
});

/**
 * CsvDataが空かどうかを判定する
 * @param csvData CsvDataエンティティ
 * @returns 空の場合true
 */
export const isCsvDataEmpty = (csvData: CsvData): boolean => {
  return csvData.totalRows === 0;
};

/**
 * CsvDataから特定の列のインデックスを取得する
 * @param csvData CsvDataエンティティ
 * @param columnName 列名
 * @returns 列インデックス（見つからない場合は-1）
 */
export const getColumnIndex = (csvData: CsvData, columnName: string): number => {
  return csvData.headers.indexOf(columnName);
};

/**
 * CsvDataから特定の列の値を取得する
 * @param csvData CsvDataエンティティ
 * @param rowIndex 行インデックス
 * @param columnName 列名
 * @returns 値（見つからない場合はundefined）
 */
export const getCellValue = (
  csvData: CsvData,
  rowIndex: number,
  columnName: string
): string | undefined => {
  const colIndex = getColumnIndex(csvData, columnName);
  if (colIndex === -1 || rowIndex < 0 || rowIndex >= csvData.totalRows) {
    return undefined;
  }
  return csvData.rows[rowIndex][colIndex];
};

/**
 * CsvDataの行データをオブジェクト形式に変換する
 * @param csvData CsvDataエンティティ
 * @param rowIndex 行インデックス
 * @returns ヘッダーをキーとしたオブジェクト
 */
export const getRowAsObject = (
  csvData: CsvData,
  rowIndex: number
): Record<string, string> | undefined => {
  if (rowIndex < 0 || rowIndex >= csvData.totalRows) {
    return undefined;
  }

  const row = csvData.rows[rowIndex];
  return csvData.headers.reduce((obj, header, index) => {
    obj[header] = row[index] ?? '';
    return obj;
  }, {} as Record<string, string>);
};

/**
 * CsvDataの全行をオブジェクト配列に変換する
 * @param csvData CsvDataエンティティ
 * @returns オブジェクトの配列
 */
export const getAllRowsAsObjects = (
  csvData: CsvData
): Record<string, string>[] => {
  return csvData.rows.map((row) =>
    csvData.headers.reduce((obj, header, index) => {
      obj[header] = row[index] ?? '';
      return obj;
    }, {} as Record<string, string>)
  );
};
